/**
 * A view's records as one person read them, handed to a page that has no connection: the render page's (src/client/render.ts),
 * where what the page writes stays in it, and a posted view saved as of a day (docs/views.md#15-views-in-messages), which
 * changes no more and refuses what the page would write, post, or run.
 */

import type { Op, Rec } from "../shared/views/records";
import type { RenderInput } from "../shared/views/render";
import { LocalData } from "./data-host";

export type Ask = (name: string, params: Record<string, unknown>) => Promise<{ value: unknown } | { error: string }>;

export class GivenData extends LocalData {
  /** Queries asked and not yet answered, which a render waits for. */
  pending = 0;
  constructor(
    input: RenderInput,
    manifest: ConstructorParameters<typeof LocalData>[1],
    private readonly ask: Ask | undefined,
    /** When the view was saved as of, which makes it refuse every change. */
    private readonly savedAt?: string,
  ) {
    super(
      {
        chat: input.chat.id,
        writer: { id: input.writer.id, role: input.writer.role, viewAdmin: input.writer.viewAdmin },
        members: input.members,
        links: input.links,
      },
      manifest,
    );
    for (const [part, rows] of Object.entries(input.rows)) {
      const copy = this.parts.get(part);
      if (copy) copy.rows = structuredClone(rows) as Record<string, Record<string, Rec>>;
    }
  }
  private refuse(): never {
    throw Object.assign(
      new Error(`This view was saved as of ${this.savedAt?.slice(0, 10)}, so it no longer changes.`),
      {
        code: "forbidden",
      },
    );
  }
  override async writeData(part: string, op: Op) {
    if (this.savedAt) this.refuse();
    return super.writeData(part, op);
  }
  override async post(...args: Parameters<LocalData["post"]>) {
    if (this.savedAt) this.refuse();
    return super.post(...args);
  }
  override async call() {
    if (this.savedAt) this.refuse();
    return super.call();
  }
  override async query(name?: string, params?: Record<string, unknown>): Promise<unknown> {
    if (!this.ask || !name) return super.query();
    this.pending++;
    try {
      const answer = await this.ask(name, params ?? {});
      if ("error" in answer) throw Object.assign(new Error(answer.error), { code: "query" });
      return answer.value;
    } finally {
      this.pending--;
    }
  }
}
